import React from 'react';
import { Box, FormControl, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, TextField } from '@mui/material';
import { Control, Controller, FieldErrors, FieldPath } from 'react-hook-form';
import { PhoneInput } from 'pages/steps/Complete/PhoneInput';
import { AddressFormConfig, AddressFormConfigProp } from 'types';
import { useIsMobile } from 'hooks';

export type AddressFormProps<Model extends Record<string, string>, FormConfig extends AddressFormConfig<Model>> = {
  control: Control<Model>;
  errors: FieldErrors<Model>;
  formConfig: FormConfig;
  testId: string;
  onFieldChange: <F extends FieldPath<Model>>(fieldName: F) => void;
};

export const AddressForm = <Model extends Record<string, string>, FormConfig extends AddressFormConfig<Model>>({
  control,
  errors,
  formConfig,
  testId,
  onFieldChange,
}: AddressFormProps<Model, FormConfig>) => {
  const { salutation, country, phone } = formConfig;
  const { isMobile } = useIsMobile();

  const getFieldError = <P extends AddressFormConfigProp>(prop: P) => {
    const formField = formConfig[prop];
    const fieldError = errors[formField.field as keyof typeof errors];

    return {
      helperText: fieldError?.message ? String(fieldError?.message) : formField.helperText,
      error: !!fieldError?.message,
    } as const;
  };

  return (
    <Box sx={{ pr: isMobile ? 0 : '30%' }} data-testid={testId}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <FormControl>
            <Controller
              name={salutation.field}
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioGroup
                  row
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    onFieldChange(salutation.field);
                  }}
                >
                  {salutation.radios.map((radio) => (
                    <FormControlLabel key={radio.label} value={radio.value} control={<Radio />} label={radio.label} />
                  ))}
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name={formConfig.firstName.field}
            rules={formConfig.firstName.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.firstName.fieldType === 'text' ? formConfig.firstName.textFieldProps : {})}
                {...getFieldError('firstName')}
                name={formConfig.firstName.field}
                label={formConfig.firstName.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.firstName.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name={formConfig.lastName.field}
            rules={formConfig.lastName.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.lastName.fieldType === 'text' ? formConfig.lastName.textFieldProps : {})}
                {...getFieldError('lastName')}
                name={formConfig.lastName.field}
                label={formConfig.lastName.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.lastName.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={formConfig.companyName.field}
            rules={formConfig.companyName.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.companyName.fieldType === 'text' ? formConfig.companyName.textFieldProps : {})}
                {...getFieldError('companyName')}
                name={formConfig.companyName.field}
                label={formConfig.companyName.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.companyName.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={formConfig.addressLine1.field}
            rules={formConfig.addressLine1.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.addressLine1.fieldType === 'text' ? formConfig.addressLine1.textFieldProps : {})}
                {...getFieldError('addressLine1')}
                name={formConfig.addressLine1.field}
                label={formConfig.addressLine1.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.addressLine1.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            control={control}
            name={formConfig.addressLine2.field}
            rules={formConfig.addressLine2.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.addressLine2.fieldType === 'text' ? formConfig.addressLine2.textFieldProps : {})}
                {...getFieldError('addressLine2')}
                name={formConfig.addressLine2.field}
                label={formConfig.addressLine2.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.addressLine2.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Controller
              control={control}
              name={country.field}
              render={({ field: { onChange, value } }) => (
                <TextField
                  select
                  // for now this field will only notify user that Schweiz is preselected and can't be changed
                  disabled
                  fullWidth
                  label={country.label}
                  variant="outlined"
                  value={value}
                  onChange={onChange}
                  size="small"
                >
                  {country.options.map((country) => (
                    <MenuItem key={country.label} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={4} md={3}>
          <Controller
            control={control}
            name={formConfig.postcode.field}
            rules={formConfig.postcode.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.postcode.fieldType === 'text' ? formConfig.postcode.textFieldProps : {})}
                {...getFieldError('postcode')}
                name={formConfig.postcode.field}
                label={formConfig.postcode.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.postcode.field);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={8} md={9}>
          <Controller
            control={control}
            name={formConfig.city.field}
            rules={formConfig.city.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.city.fieldType === 'text' ? formConfig.city.textFieldProps : {})}
                {...getFieldError('city')}
                name={formConfig.city.field}
                label={formConfig.city.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.city.field);
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            control={control}
            name={formConfig.email.field}
            rules={formConfig.email.registerOptions}
            render={({ field: { onChange, value } }) => (
              <TextField
                {...(formConfig.email.fieldType === 'text' ? formConfig.email.textFieldProps : {})}
                {...getFieldError('email')}
                name={formConfig.email.field}
                label={formConfig.email.label}
                value={value || ''}
                onChange={(e) => {
                  onChange(e);
                  onFieldChange(formConfig.email.field);
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name={phone.field}
            control={control}
            rules={phone.registerOptions}
            render={({ field: { value, onChange } }) => (
              <PhoneInput
                label={phone.label}
                value={value}
                {...getFieldError('phone')}
                onChange={(phoneValue) => {
                  onChange(phoneValue);
                  onFieldChange(phone.field);
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
